import * as React from 'react';
import {connect} from 'react-redux';
import {Button, Modal, Tooltip, OverlayTrigger} from 'react-bootstrap';
import Select from 'react-select';
import * as FormsyComponents from 'formsy-react-components';
import Formsy from 'formsy-react';
import * as _ from 'underscore';
import URI from 'urijs';
import moment from 'moment';

import {Subheading} from './subheading';
import LoadingPane from './loadingpane';
import {addErrorMessage} from '../model/feedback';
import * as Locale from '../model/locale';
import {CABINET_ROUTE_LISTING} from '../model/routes/main';
import {
    CalibrationStatusTypes, createCalibrationStatus, resetCalibrationProcess,
    CalibrationStatusParameters, getCalibrationStatusByPremises
} from '../model/calibration';
import {reloadPage} from '../model/core/browser';
import {routeActions} from "redux-simple-router";

/**
 * State of the table row based on calibration status
 */
export class CalibrationRowState {

    state: string;

    constructor(status: any) {
        this.state = status ? status.status_type : null;
    }

    isError() {
        return this.state === CalibrationStatusTypes.STATUS_ERROR;
    }

    isFailed() {
        return this.state === CalibrationStatusTypes.STATUS_FAIL;
    }

    canRetry() {
        return this.isFailed() || this.state === CalibrationStatusTypes.STATUS_CANCELLED;
    }

    isInProgress() {
        return this.state === CalibrationStatusTypes.STATUS_IN_PROGRESS;
    }

    isReady() {
        return this.state == null;
    }

    isSuccessful() {
        return this.state === CalibrationStatusTypes.STATUS_SUCCESS;
    }

    toString() {
        return this.state;
    }
}

/*
 * List of data points in format required for select box
 */
function selectOptions(optionsList: any) {
    let options = [];
    optionsList.forEach(function (option) {
        options.push({
            label: option.name || option.description,
            value: option.id || option.unit_id});
    });
    // keep sorted by name
    optionsList.sort(function (option1, option2) {
        return option1.label > option2.label;
    });
    return options;
}

export interface CalibrationTableRowProps extends React.Props<CalibrationTableRowProps> {
    link?: string;
    textForKey?: any;
    dispatch?: any;
    premisesUnit?: any;
    availableCalibrationUnits?: [any];
    assignedCalibrationUnit?: any;
    onPremisesCalibrationUnitSelect?: Function;
    onUnitStatusChanged?: Function;
    loading?: any;
    calibrationStatus?: any;
    location?: any;
}

export class CalibrationTableRow extends React.Component<CalibrationTableRowProps, any> {

    state: {
        link?: string;
        textForKey?: any;
        dispatch?: any,
        showCancelModal?: boolean;
        stateChanging?: boolean;
    };

    constructor(props) {
        super(props);
        this.state = {
            showCancelModal: false,
            stateChanging: false,
            link: ''
        };
    }

    /**
     * Returns the unit ID for the currently assigned calibration unit, if any
     * @returns {any|String|string}
     */
    getAssignedUnitId() {
        let {assignedCalibrationUnit} = this.props;
        return assignedCalibrationUnit ? assignedCalibrationUnit.unit_id : null;
    }

    /**
     * Event: when user changes the currently selected calibration unit for the row
     * @param selection
     */
    onCalibrationUnitSelect(selection) {
        selection = selection ? selection.value : null;
        this.props.onPremisesCalibrationUnitSelect(
            this.props.premisesUnit.unit_id,
            selection,
            this.getAssignedUnitId()
        );
        this.setState({
            link: selection ? `?extra_ids=` + selection : ``
        });
    }
    /**
     * Changes the calibration status for the current row data
     *
     * @param payload
     */
    changeCalibrationStatus(payload) {
        let {dispatch, onUnitStatusChanged} = this.props;
        let self = this;
        this.setState({stateChanging: true});
        let callback = (response) => {
            let responseData = response || {};
            if (response.data) {
                responseData = JSON.parse(response.data);
            }
            if (responseData.status_type) {
                onUnitStatusChanged(response);
            }
            self.setState({
                stateChanging: false
            });
        };
        dispatch(createCalibrationStatus(payload)).then(callback).catch(callback);
    }

    /**
     * Event: starts the calibration process for the currently set unit ID
     */
    startCalibrationAction() {
        let {premisesUnit} = this.props;
        let payload: CalibrationStatusParameters = {
            unit_id: premisesUnit.unit_id,
            calibration_unit_id: this.getAssignedUnitId(),
            status_type: CalibrationStatusTypes.STATUS_IN_PROGRESS,
            metadata: {}
        };
        this.changeCalibrationStatus(payload);
    }

    /**
     * Event: when user attempts to cancel in progress calibration, show modal
     */
    cancelCalibration() {
        this.setState({showCancelModal: true});
    }

    hideCancelModal() {
       this.setState({showCancelModal: false});
    }

    /**
     * Cancels any in-progress status for this row
     */
    cancelAction() {
        let {premisesUnit} = this.props;
        let payload: CalibrationStatusParameters = {
            unit_id: premisesUnit.unit_id,
            calibration_unit_id: this.getAssignedUnitId(),
            status_type: CalibrationStatusTypes.STATUS_CANCELLED,
            metadata: {}
        };
        this.hideCancelModal();
        this.changeCalibrationStatus(payload);
    }

    makeUnitCol(premisesUnit, rowState, calibrationStatus) {
        let unitLink = `${CABINET_ROUTE_LISTING}${premisesUnit.unit_id}`;
        let to, from;
        if (this.state.link && !rowState.isInProgress()) {
            unitLink += this.state.link;
        } else if (calibrationStatus) {
            let fromDate = () => moment(calibrationStatus.time_created);
            // look at readings up to 1 hour from start of calibration
            // FIXME: use range in configuration from calibration service
            const calibrationPeriod = 1;
            if (rowState.isInProgress()) {
                to = fromDate().add(calibrationPeriod, 'h').unix();
                from = fromDate().unix();
                unitLink += `?extra_ids=${calibrationStatus.calibration_unit_id}&to=${to}&from=${from}`;
            } else if (rowState.isFailed() || rowState.isSuccessful()) {
                to = fromDate().unix();
                from = fromDate().subtract(calibrationPeriod, 'h').unix();
                unitLink += `?extra_ids=${calibrationStatus.calibration_unit_id}&to=${to}&from=${from}`;
            }
        }
        return (
            <div className="col-premises-unit">
                <a href={unitLink} target="_blank">{premisesUnit.imei} ({premisesUnit.description || premisesUnit.unit_id})</a>
            </div>);
    }
    makeCalibrationUnitCol(calibrationUnitOptions, stateChanging, rowState, textForKey) {
        return (
            <div className="col-calibration-unit">
                <Select
                    name="text"
                    options={calibrationUnitOptions}
                    addLabelText="label"
                    clearable={true}
                    disabled={stateChanging || rowState.isInProgress()}
                    placeholder={textForKey(Locale.TEXT_CALIBRATION_SELECT_CALIBRATION_UNIT)}
                    noResultsText={textForKey(Locale.TEXT_CALIBRATION_SELECT_NO_UNITS_AVAILABLE)}
                    onChange={this.onCalibrationUnitSelect.bind(this)}
                    value={this.getAssignedUnitId()}
                />
            </div>
        );
    }
    makeStatusCol(rowState, calibrationStatus, assignedCalibrationUnit, stateChanging, textForKey, premisesUnit) {
        let statusClass = null;
        let statusText = null;
        let statusButton = null;
        let buttonText = null;
        let buttonClick = null,
            tooltip_data = {};

        if (rowState.isInProgress()) {
            statusClass = 'in-progress';
            statusText = Locale.TEXT_CALIBRATION_UNIT_STATUS_IN_PROGRESS;
            buttonText = Locale.TEXT_CALIBRATION_BUTTON_CANCEL;
            buttonClick = this.cancelCalibration.bind(this);
        }
        else if (rowState.isSuccessful()) {
            statusClass = 'pass';
            statusText = Locale.TEXT_CALIBRATION_UNIT_STATUS_PASS;
            buttonText = Locale.TEXT_CALIBRATION_BUTTON_START;
            buttonClick = this.startCalibrationAction.bind(this);
            if (calibrationStatus.metadata) {
                tooltip_data[Locale.TEXT_CALIBRATION_METADATA_OFFSET_LABEL] = calibrationStatus.metadata.offset;
            }
        }
        else if (rowState.isError()) {
            statusClass = 'error';
            statusText = Locale.TEXT_CALIBRATION_UNIT_STATUS_ERROR;
            buttonText = Locale.TEXT_CALIBRATION_BUTTON_RETRY;
            buttonClick = this.startCalibrationAction.bind(this);
            if (calibrationStatus.metadata) {
                tooltip_data[Locale.TEXT_CALIBRATION_UNIT_STATUS_ERROR] = calibrationStatus.metadata.detail;
                if (calibrationStatus.metadata.invalid_offset) {
                    tooltip_data[Locale.TEXT_CALIBRATION_METADATA_OFFSET_LABEL] = calibrationStatus.metadata.invalid_offset;
                }
            }
        }
        else if (rowState.canRetry()) {
            statusClass = 'fail';
            if (rowState.isFailed()) {
                statusText = Locale.TEXT_CALIBRATION_UNIT_STATUS_FAIL;
                if (calibrationStatus.metadata) {
                    tooltip_data[Locale.TEXT_CALIBRATION_UNIT_STATUS_ERROR] = calibrationStatus.metadata.error;
                    if (calibrationStatus.metadata.invalid_offset) {
                        tooltip_data[Locale.TEXT_CALIBRATION_METADATA_OFFSET_LABEL] = calibrationStatus.metadata.invalid_offset;
                    }
                }
            } else {
                statusText = Locale.TEXT_CALIBRATION_UNIT_STATUS_CANCELLED;
            }
            buttonText = Locale.TEXT_CALIBRATION_BUTTON_RETRY;
            buttonClick = this.startCalibrationAction.bind(this);
        }
        else {
            statusClass = 'idle';
            if (rowState.isReady()) {
                buttonText = Locale.TEXT_CALIBRATION_BUTTON_START;
                buttonClick = this.startCalibrationAction.bind(this);
            }
        }

        // can only change status if a unit has been assigned
        if (assignedCalibrationUnit != null && buttonText != null) {
            statusButton = (
                <Button onClick={buttonClick} disabled={stateChanging}>
                    {textForKey(buttonText)}
                </Button>
            );
        }
        let tooltip;
        if (calibrationStatus) {
            tooltip_data[Locale.TEXT_TIME] = calibrationStatus.time_created;
            let tooltipBody = [];
            _.each(tooltip_data as any, (value, key) => {
                tooltipBody.push(<div className="tooltip-row">
                    <div className="tooltip-row-title">{textForKey(key)}:</div>
                    <div className="tooltip-row-data">{value}</div>
                </div>);
            });
            tooltip = <Tooltip id={premisesUnit.unit_id}>
                <div className="tooltip-data">
                    {tooltipBody}
                </div>
            </Tooltip>;
        }
        let statusCol = <div className={"col-calibration-status " + statusClass}>
            <div className="progress-text">
                {textForKey(statusText)}
            </div>
            {statusButton}
        </div>;
        return calibrationStatus ?
            <OverlayTrigger placement="top" overlay={tooltip} trigger={['hover', 'touch', 'click']}>
                {statusCol}
            </OverlayTrigger> : statusCol ;
    }
    render() {
        let {
            textForKey, availableCalibrationUnits, premisesUnit,
            assignedCalibrationUnit, calibrationStatus} = this.props;
        let {stateChanging} = this.state;
        let rowState = new CalibrationRowState(calibrationStatus);
        let calibrationUnitOptions = selectOptions(availableCalibrationUnits);

        let cancelModalProps = {
            textForKey: this.props.textForKey,
            onSubmit: this.cancelAction.bind(this),
            state: {
                show: this.state.showCancelModal,
                onHide: this.hideCancelModal.bind(this),
            }
        };

        return (
            <div className="row row-multiple">
                <CancelCalibrationModal ref="cancel" {...cancelModalProps}/>
                {this.makeUnitCol(premisesUnit,rowState,calibrationStatus)}
                {this.makeCalibrationUnitCol(calibrationUnitOptions, stateChanging, rowState, textForKey)}
                {this.makeStatusCol(rowState, calibrationStatus, assignedCalibrationUnit, stateChanging, textForKey, premisesUnit)}
            </div>);
    }
}


interface CalibrationProps {
    dispatch?: Function;
    textForKey?: Function;
    premises: [any];
    loading: boolean;
    calibrationUnits: any;
    premisesUnits?: any;
    unit?: any;
    location?: any;
}

export class Calibration extends React.Component<CalibrationProps, any> {
    private uri: uri.URI;
    state: {
        selectedPremises: any;
        showResetModal: boolean;
        premisesUnits: any;
        selectedCalibrationUnitIds: any;
        inUseCalibrationUnitIds: any;
        calibrationStatusList: any;
    };

    constructor(props) {
        super(props);
        let {location} = this.props;
        this.uri = URI((location.pathname ? location.pathname : '') + location.search);
        this.state = {
            selectedPremises: null,
            showResetModal: false,
            premisesUnits: null,
            selectedCalibrationUnitIds: {},
            inUseCalibrationUnitIds: new Set(),
            calibrationStatusList: []
        };
    }

    componentDidMount() {
        // Selects the premises directly after the component is mounted into the DOM
        if (this.props.location.query.premisesId) {
            this.onPremisesSelect({value: parseInt(this.props.location.query.premisesId)});
        }
    }

    reset() {
        this.setState({showResetModal: true});
    }

    hideResetModal() {
        this.setState({showResetModal: false});
    }

    /**
     * Cancels all calibrations that the user has access to
     */
    cancelAllCalibrations() {
        let {dispatch} = this.props;
        this.hideResetModal();
        dispatch(resetCalibrationProcess()).then(function () {
            reloadPage();
        });
    }

    /**
     * Event: when user selects a premises to view
     *
     * @param selection
     */
    onPremisesSelect(selection) {
        let {dispatch} = this.props;
        let {inUseCalibrationUnitIds} = this.state;
        let self = this;
        let premisesId = selection.value;
        // update when user selects premises
        this.setState({
            selectedPremises: premisesId
        });

        this.uri.setQuery('premisesId', premisesId);
        this.props.dispatch(routeActions.push(this.uri.toString()));
        // update units for selected premises
        dispatch(getCalibrationStatusByPremises(premisesId)).then((statuses) => {
            let selectedCalibrationIds = {};
            statuses.forEach((status) => {
                let curUnitId = status.calibration_unit_id;
                if (status.status_type === CalibrationStatusTypes.STATUS_IN_PROGRESS) {
                    selectedCalibrationIds[curUnitId] = status.unit_id;
                    inUseCalibrationUnitIds.add(curUnitId);
                }
            });
            self.setState({
                selectedCalibrationUnitIds: selectedCalibrationIds,
                calibrationStatusList: statuses || []
            });
        });
    }

    /**
     * Event: when current status for a unit is modified
     *
     * @param newStatus
     */
    onUnitStatusChanged(newStatus) {
        let {calibrationStatusList, inUseCalibrationUnitIds} = this.state;
        let replaced = false;
        // update any existing status in local memory
        for (let i = 0; i < calibrationStatusList.length; i++) {
            if (calibrationStatusList[i].unit_id === newStatus.unit_id) {
                calibrationStatusList.splice(i, 1, newStatus);
                replaced = true;
            }
        }
        // or add to list if no existing status
        if (!replaced) {
            calibrationStatusList.push(newStatus);
        }
        // update in-use list
        if (newStatus.status_type === CalibrationStatusTypes.STATUS_IN_PROGRESS) {
            inUseCalibrationUnitIds.add(newStatus.calibration_unit_id);
        }
        else {
            inUseCalibrationUnitIds.delete(newStatus.calibration_unit_id);
        }
        this.setState({calibrationStatusList: calibrationStatusList});
    }

    /**
     * Event: when user selects a new calibration unit for the given unit ID,
     * replacing the previous calibration unit.
     *
     * @param unitId
     * @param calibrationUnitId
     * @param previousCalibrationUnitId
     */
    onPremisesCalibrationUnitSelect(unitId, calibrationUnitId, previousCalibrationUnitId) {
        let {selectedCalibrationUnitIds, inUseCalibrationUnitIds} = this.state;
        // any calibration unit IDs in use should not be changeable
        if (inUseCalibrationUnitIds.has(calibrationUnitId)) {
            this.props.dispatch(addErrorMessage(Locale.TEXT_ERROR_CALIBRATION_NOT_ALLOWED));
            return;
        }

        // if the select is cleared
        if (calibrationUnitId == null && previousCalibrationUnitId !== null) {
            delete selectedCalibrationUnitIds[previousCalibrationUnitId];
        }
        // initial selection
        else if (previousCalibrationUnitId == null) {
            selectedCalibrationUnitIds[calibrationUnitId] = unitId;
        }
        // updated selection
        else if (previousCalibrationUnitId !== calibrationUnitId) {
            selectedCalibrationUnitIds[calibrationUnitId] = unitId;
            delete selectedCalibrationUnitIds[previousCalibrationUnitId];
        }

        this.setState({
            selectedCalibrationUnits: selectedCalibrationUnitIds
        });
    }

    render() {
        let {loading, textForKey, premises} = this.props;
        let premisesOptions = selectOptions(premises);

        if (loading) {
            return (<LoadingPane title={textForKey(Locale.TEXT_NAVIGATION_CALIBRATION)}/>);
        }
        let resetButton = null;
        if (this.state.selectedPremises != null) {
            resetButton = (
                <Button bsStyle="reset"
                        onClick={this.reset.bind(this)}>
                    {textForKey(Locale.TEXT_CALIBRATION_BUTTON_RESET)}
                </Button>);
        }
        let tableHead = (
            <div className="list-head clearfix">
                <div className="head-premises-unit">
                    {textForKey(Locale.TEXT_CALIBRATION_TABLE_HEAD_UNIT_PREMISES)}
                </div>
                <div className="head-calibration-unit">
                    {textForKey(Locale.TEXT_CALIBRATION_TABLE_HEAD_UNIT_CALIBRATION)}
                </div>
                <div className="head-calibration-status">
                    {textForKey(Locale.TEXT_CALIBRATION_TABLE_HEAD_STATUS)}
                </div>
            </div>
        );

        let calibrationTable = null;
        if (this.state.selectedPremises != null) {
            calibrationTable = (
                <div className='list-view'>
                    {tableHead}
                    {this.buildCalibrationRows()}
                </div>
            );
        }

        let resetModalProps = {
            textForKey: this.props.textForKey,
            onSubmit: this.cancelAllCalibrations.bind(this),
            state: {
                show: this.state.showResetModal,
                onHide: this.hideResetModal.bind(this),
            }
        };
        let resetModal = <CancelCalibrationModal ref="cancel" {...resetModalProps}/>;
        return (
            <div className="calibration container content">
                <Subheading title={textForKey(Locale.TEXT_NAVIGATION_CALIBRATION)}/>
                <div className="premises-selection-wrapper">
                    <div className="premises-selection">
                        <Select
                            name="text"
                            options={premisesOptions}
                            addLabelText="label"
                            clearable={false}
                            placeholder={textForKey(Locale.TEXT_CALIBRATION_SELECT_PREMISES)}
                            noResultsText={textForKey(Locale.TEXT_CALIBRATION_SELECT_NO_PREMISES_AVAILABLE)}
                            onChange={this.onPremisesSelect.bind(this)}
                            value={this.state.selectedPremises}
                        />
                    </div>
                    {resetButton}
                </div>
                {calibrationTable}
                {resetModal}
            </div>
        );
    }

    private buildCalibrationRows() {
        let {premisesUnits, dispatch, calibrationUnits} = this.props;
        let {selectedCalibrationUnitIds, calibrationStatusList} = this.state;
        let selectedUnitPairs = _.pairs(selectedCalibrationUnitIds);
        return premisesUnits.map(unit => {
            let unitId = unit.unit_id;

            // a selected calibration unit takes precedence over any existing
            // calibration unit defined server-side (in the case where user
            // is changing unit to new location)
            let selectedCalibrationUnitId = _.first(
                _.flatten(
                    selectedUnitPairs.filter((pair) => {
                        return pair[1] === unitId;
                    })
                )
            );
            let curCalibrationUnit = null;
            if (selectedCalibrationUnitId) {
                curCalibrationUnit = calibrationUnits.find(
                    unitData => unitData.unit_id === selectedCalibrationUnitId
                );
            }

            let curStatus = calibrationStatusList.find(
                unitData => unitData.unit_id === unitId
            );
            let calibrationRowProps = {
                textForKey: this.props.textForKey,
                premisesUnit: unit,
                assignedCalibrationUnit: curCalibrationUnit,
                dispatch: dispatch,
                availableCalibrationUnits: calibrationUnits || [],
                onUnitStatusChanged: this.onUnitStatusChanged.bind(this),
                onPremisesCalibrationUnitSelect: this.onPremisesCalibrationUnitSelect.bind(this),
                calibrationStatus: curStatus,
            };
            return (
                <CalibrationTableRow key={unitId} {...calibrationRowProps} />
            );
        });
    }
}



interface CancelCalibrationModalProps extends React.Props<CancelCalibrationModalProps> {
    textForKey: Function;
    canSubmit?: boolean;
    onSubmit?: Function;
    state: {
        show: boolean;
        onHide: Function;
    };
}

export class CancelCalibrationModal extends React.Component<CancelCalibrationModalProps, any> {

    initialState: {
        canSubmit: boolean;
    };

    state: {
        canSubmit?: boolean;
    };

    constructor() {
        super();
        this.initialState = {canSubmit: false};
        this.state = this.initialState;
    }

    onHide() {
        this.props.state.onHide();
        this.state = this.initialState;
    };

    onSubmit() {
        let {canSubmit} = this.state;
        if (canSubmit) {
            this.props.onSubmit();
            this.state = this.initialState;
        }
    }

    render() {
        let {textForKey, state} = this.props;
        // Set form text
        let cancelText = textForKey(Locale.TEXT_MODAL_BUTTON_CANCEL);
        let submitText = textForKey(Locale.TEXT_MODAL_BUTTON_CONTINUE);
        // set the checkbox
        let checkbox = (
            <div>
                <FormsyComponents.Checkbox
                    label={textForKey(Locale.TEXT_MODAL_CALIBRATION_CANCEL_LABEL_CANCEL)}
                    name="cancel" onChange={this.toggleCheckbox.bind(this)}/>
            </div>
        );
        return (
            <Modal bsClass="cancel-calibration-holder"
                   show={state.show}
                   onHide={this.onHide.bind(this)}>
                <Formsy.Form className="form-horizontal">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {textForKey(Locale.TEXT_MODAL_TITLE_CALIBRATION_CANCEL)}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div>
                            {checkbox}
                        </div>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button bsSize="large" onClick={this.onHide.bind(this)}>
                            {cancelText}
                        </Button>
                        <Button bsStyle="primary"
                                bsSize="large"
                                onClick={this.onSubmit.bind(this)}
                                disabled={!this.state.canSubmit}>
                            {submitText}
                        </Button>
                    </Modal.Footer>
                </Formsy.Form>
            </Modal>
        );
    }

    private toggleCheckbox() {
        let {canSubmit} = this.state;
        this.setState({canSubmit: !canSubmit});
    }
}

export var CalibrationConnected = connect(
    state => ({
        textForKey: state.data.textForKey,
        premises: state.data.premises.results,
        userData: state.data.user,
        calibrationUnits: state.data.calibration.units,
        premisesUnits: state.data.premisesUnits
    })
)(Calibration);
